/* eslint-disable camelcase */
import { d3, cropr, gettext } from "../adapter";
import each from "lodash/each";
import extend from "lodash/extend";
import moment from "moment";
import timeFormat from "./chartTimeFormat";
import weatherStationActions from "./../actions/weatherStationActions";

// @TODO: precipitationInterval CACHE AND NEW DATA
// For the tooltip
var bisectDate = d3.bisector(function (d) {
  return d.timestamp;
}).left;

// The constructor
class Chart {
  constructor(options, data, dispatch) {
    // Empty data, this is our data format
    this.data = { values: [] };
    this.precipitationInterval = 0;
    this.stationId = false;
    this.dispatch = dispatch;

    // Default options
    this.options = {
      id: false,
      class: false,
      width: 500,
      height: 350,
      i18n: {
        unit: cropr.l10n.unit_precipitation_display, //I18n.t('units.mm.short'),
        unitBig: cropr.l10n.unit_precipitation_display, // I18n.t('units.mm.long'),
      },
    };

    // Merge the options
    this.options = extend(this.options, options);

    // Set chart dimension, also depending on settings as brush
    var margin = { top: 10, right: 70, bottom: 30, left: 70 };
    var height = this.options.height - (margin.top + margin.bottom);
    var width = this.options.width - (margin.left + margin.right);

    this.chart = {
      width: width,
      height: height,
      margin: margin,
    };

    this.totalChart = {
      height: this.chart.height,
      margin: {
        top: this.chart.margin.top,
      },
    };

    // Set the data
    if (data) {
      this.setData(data);
    }

    // No SVG and elements yet
    this.elements = {};

    var _this = this;
    var scrolling = false;
    var x;
    this.zoom = d3.behavior
      .zoom()
      .x(this.scales.x)
      .on("zoom", function () {
        if (_this.scales.x.domain()[0] < _this.data.minDate) {
          x =
            _this.zoom.translate()[0] -
            _this.scales.x(_this.data.minDate) +
            _this.scales.x.range()[0];
          _this.zoom.translate([x, 0]);
        } else if (_this.scales.x.domain()[1] > _this.data.maxDate) {
          x =
            _this.zoom.translate()[0] -
            _this.scales.x(_this.data.maxDate) +
            _this.scales.x.range()[1];
          _this.zoom.translate([x, 0]);
        }

        // Execute only once
        if (!scrolling) {
          _this.elements.eventRect.classed("grabbing", true);
          _this.dispatch(weatherStationActions.hovered(false));
        }

        scrolling = true;
        _this._scroll();
      })
      .on("zoomend", function () {
        if (!scrolling) return;

        _this.elements.eventRect.classed("grabbing", false);

        // Enable the pin hovers again when we stop zooming
        if (this === d3.event.sourceEvent.target) {
          _this.dispatch(weatherStationActions.hovered(true));
        }

        scrolling = false;
        _this.dispatch(
          weatherStationActions.brushDomain([
            _this.scales.x.domain()[0],
            _this.scales.x.domain()[1],
          ])
        );
      });
  }
}

// Init graph
Chart.prototype.init = function (el) {
  var _this = this;

  // Only init once and only init if we have data
  if (this.svg || !this.data) {
    return false;
  }

  // Create the SVG
  this.elements.svg = d3
    .select(el)
    .append("svg")
    .attr("class", this.options.class)
    .attr("id", this.options.id)
    .attr("width", this.options.width)
    .attr("height", this.options.height);

  var chart = (this.elements.chart = this.elements.svg
    .append("g")
    .attr("class", "precipitation chart")
    .attr("transform", "translate(" + this.chart.margin.left + "," + this.chart.margin.top + ")"));

  this.elements.innerChart = this.elements.svg
    .append("g")
    .attr("class", "precipitation inner")

    .attr("clip-path", "url(#clip-" + this.options.id + ")")
    .attr("transform", "translate(" + this.chart.margin.left + "," + this.chart.margin.top + ")");

  this.elements.grid = {
    x: chart
      .append("g")
      .attr("class", "precipitation grid x")
      .attr("transform", "translate(0," + this.chart.height + ")"),
    y: chart.append("g").attr("class", "precipitation grid y"),
  };

  // Draw the axis
  this.elements.axis = {
    x: chart
      .append("g")
      .attr("class", "precipitation axis x")
      .attr("transform", "translate(0," + this.chart.height + ")"),
    y: chart.append("g").attr("class", "precipitation axis y"),
  };

  // this.elements.bars = this.elements.innerChart.selectAll('.bar.stack');

  this.elements.legendList = d3
    .select(el)
    .append("ul")
    .attr("class", "l-stacked legend has-left-spacing");
  this.elements.legendPrecipitation = this.elements.legendList.append("li").attr("class", "legend");
  var precipitationSpan = this.elements.legendPrecipitation
    .append("span")
    .attr("class", "precipitation");
  precipitationSpan.append("span").attr("class", "precipitation1");
  precipitationSpan.append("span").attr("class", "precipitation2");
  precipitationSpan.append("span").attr("class", "precipitation3");

  this.elements.legendSpan = this.elements.legendPrecipitation
    .append("span")
    .attr("class", "content")
    .html(
      gettext("Precipitation in {{unit}} per {{interval}} hour")
        .replace("{{unit}}", cropr.l10n.unit_precipitation_display)
        .replace("{{interval}}", this.precipitationInterval)
    );

  this.elements.chartCover = this.elements.svg
    .append("g")
    .attr("class", "cover")
    .attr(
      "transform",
      "translate(" + this.chart.margin.left + "," + this.totalChart.margin.top + ")"
    );

  // Draw the mouse vertical line
  var clipId = "clip";

  // Draw the mouse vertical line
  this.elements.mouse = this.elements.chartCover
    .append("g")
    .attr("clip-path", "url(#" + clipId + "-" + this.options.id + ")")
    .append("g")
    .attr("class", "mouse")
    .style("opacity", "0");

  this.elements.mouse
    .append("line")
    .attr("class", "line mouse")
    .attr("x1", 2)
    .attr("y1", 8)
    .attr("x2", 2)
    .attr("y2", this.totalChart.height);

  this.elements.mouse
    .append("circle")
    .attr("class", "circle mouse")
    .attr("cx", 2)
    .attr("cy", 0)
    .attr("r", 6);

  this.elements.tooltip = this.elements.mouse.append("g").attr("class", "dacom-online-tooltip");
  this.elements.tooltipBalloon = this.elements.tooltip.insert("rect").attr("rx", 2).attr("ry", 2);

  //  this.elements.tooltipText = this.elements.tooltip.append('text').attr('class', 'tooltipText');
  this.elements.tooltip1 = this.elements.tooltip.append("g").attr("class", "dacom-online-tooltip");
  this.elements.tooltipText1 = this.elements.tooltip1.append("text").attr("class", "tooltipText");

  this.elements.tooltip2 = this.elements.tooltip.append("g").attr("class", "dacom-online-tooltip");
  this.elements.tooltipText2 = this.elements.tooltip2.append("text").attr("class", "tooltipText");

  this.elements.eventRect = this.elements.svg
    .append("svg:rect")
    .attr("class", "rect event")
    .attr("width", this.chart.width)
    .attr(
      "transform",
      "translate(" + this.chart.margin.left + "," + this.totalChart.margin.top + ")"
    )
    .attr("height", this.totalChart.height)
    .attr("fill", "none")
    .attr("pointer-events", "all")
    .on("mouseout", function () {
      // on mouse out hide line, circles and text
      _this.dispatch(weatherStationActions.hovered(false));
    })
    .on("mouseover", function () {
      // on mouse in show line, circles and text
      _this.dispatch(weatherStationActions.hovered(true));
    })
    .on("mousemove", function () {
      // mouse moving over canvas
      //if(d3.event.which) return;
      var mouseX = d3.mouse(this)[0]; // mouse position in x
      var date = _this.scales.x.invert(mouseX); // date corresponding to mouse x
      _this.dispatch(weatherStationActions.hoverDate(date));
    })
    .on("click", function () {
      if (d3.event.defaultPrevented) return;
      _this.dispatch(weatherStationActions.togglePin());
    })
    .call(this.zoom)
    .on("wheel.zoom", null)
    .on("MozMousePixelScroll.zoom", null)
    .on("mousewheel.zoom", null)
    .on("dblclick.zoom", null);

  // Draw the clippaths
  this._drawClipPaths();
  this.update();
};

// Set the data we need, we expect to receive it in the correct format
Chart.prototype.setData = function (data) {
  // Set the data
  this.data = extend(this.data, data);

  // Set min and max data value
  this.data.minDate = this.data.station.measurements.airTemperature[0].timestamp;
  this.data.maxDate =
    this.data.station.measurements.airTemperature[
      this.data.station.measurements.airTemperature.length - 1
    ].timestamp;

  // Create or update scales and axis etc.
  this._setScales();
  this._setAxis();

  this._setDataShapes();
};

Chart.prototype.updateHoverState = function (hovered, pinned, pinDate) {
  if (hovered && !pinned) {
    this.elements.mouse.style("opacity", ".5");
    this.elements.eventRect.classed("grab", false);
  } else if (!pinned) {
    this.elements.mouse.style("opacity", "0");
  } else {
    this.data.pinDate = pinDate;
    this.elements.mouse.style("opacity", "1");
    this.elements.eventRect.classed("grab", true);
  }

  this.data.pinned = pinned;
};

Chart.prototype.updateHoverDate = function (date, pinned) {
  if (!pinned) {
    var xPos = this.scales.x(date);
    this.elements.mouse.attr("transform", "translate(" + (xPos - 2) + "," + 0 + ")");

    var data = this.data.compressedData;
    var i = bisectDate(data, date, 1);
    var d0 = data[i - 1];
    var d1 = data[i];
    var d = d1 && date - d0.timestamp > d1.timestamp - date ? d1 : d0;

    var dtool = new Date(date);

    if (date) {
      if (this.data.station.showFiveMinutesMeasurements === true) {
        dtool.setMinutes(Math.floor(date.getMinutes() / 5) * 5);
      } else {
        dtool.setMinutes(Math.floor(date.getMinutes() / 15) * 15);
      }
    }
    this.elements.tooltipText1.text(moment(dtool).format("L LT"));
    this.elements.tooltipText2.text(
      cropr.l10n_utils.mm_to_l10n(d.y) + " " + cropr.l10n.unit_precipitation_display
    );

    // Calculate the bbox
    var tooltipMarginLeftRight = 5; // 5px margin
    var tooltipMarginTopBottom = 4; // 5px margin

    if (xPos < this.chart.width - this.chart.margin.right - 20) {
      this.elements.tooltip1.attr("transform", "translate(21, 9)");
      this.elements.tooltipText1.attr("text-anchor", "left");
      this.elements.tooltip2.attr("transform", "translate(21, 24)");
      this.elements.tooltipText2.attr("text-anchor", "left");
    } else {
      this.elements.tooltip1.attr("transform", "translate(-20, 9)");
      this.elements.tooltipText1.attr("text-anchor", "end");
      this.elements.tooltip2.attr("transform", "translate(-20, 24)");
      this.elements.tooltipText2.attr("text-anchor", "end");
    }

    var bbox1 = this.elements.tooltipText1[0][0].getBBox();
    var bbox2 = this.elements.tooltipText2[0][0].getBBox();

    if (bbox1.width > bbox2.width) {
      var bboxWidth = bbox1.width + 2 * tooltipMarginLeftRight;
      var bboxX = bbox1.x;
    } else {
      bboxWidth = bbox2.width + 2 * tooltipMarginLeftRight;
      bboxX = bbox2.x;
    }

    this.elements.tooltipBalloon
      .attr("y", bbox1.y - tooltipMarginTopBottom + 9)
      .attr("width", bboxWidth)
      .attr("height", bbox1.height * 2 + 2 * tooltipMarginTopBottom);

    if (xPos < this.chart.width - this.chart.margin.right - 20) {
      this.elements.tooltipBalloon.attr("x", bboxX - tooltipMarginLeftRight + 20);
    } else {
      this.elements.tooltipBalloon.attr("x", bboxX - tooltipMarginLeftRight - 20);
    }
  }
};

Chart.prototype.getColor = function (yval) {
  var blue_color_array = [
    "#004080",
    "#004d99",
    "#0059b3",
    "#0066cc",
    "#0073e6",
    "#0080ff",
    "#1a8cff",
    "#1e90ff",
    "#3399ff",
    "#4da6ff",
    "#66b3ff",
    "#80bfff",
    "#99ccff",
    "#b3d9ff",
    "#cce6ff",
    "#e6f2ff",
  ];

  if (yval % 0.2 === 0 && yval <= 1.0) {
    yval = yval * 5;
  } else {
    yval = Math.round(yval);
  }
  var color_idx = yval <= blue_color_array.length ? yval - 1 : blue_color_array.length - 1;

  return blue_color_array[color_idx];
};

// Update
Chart.prototype.update = function (data) {
  var _this = this;

  if (data) {
    // DIE SETDATA FUNCTIE BETER BEKIJKEN
    this.setData(data);
    this.zoom.x(this.scales.x);
    this.scales.x.domain(data.domain);
  }

  var redraw = false;
  if (this.data.station.id !== this.stationId) {
    redraw = true;
    this.stationId = this.data.station.id;
  }

  if (
    this.data.station.showAllMeasurements === true ||
    this.data.station.showFiveMinutesMeasurements === true ||
    this.data.station.measurements.precipitation.length < 200
  ) {
    var compressedData = this.data.station.measurements.precipitation;
    this.data.compressedData = compressedData;
  } else {
    compressedData = this._compressBatched(this.data.station.measurements.precipitation, redraw);
  }

  var maxYDomain = d3.max(
    compressedData.map(function (d) {
      return d.y;
    })
  );

  if (!maxYDomain) maxYDomain = 5;
  this.scales.y.domain([0, maxYDomain]);

  var selection = this.elements.innerChart.selectAll(".bar.stack").data(compressedData);
  selection.enter().append("rect").attr("class", "precipitation bar stack");
  selection.exit().remove();

  this.elements.axis.x.transition().call(this.axis.x);
  this.elements.axis.y.transition().call(this.axis.y);
  this.elements.grid.x.transition().call(this.axis.gridX);
  this.elements.grid.y.transition().call(this.axis.gridY);

  this.elements.eventRect.attr("width", this.chart.width);

  selection
    .transition()
    .attr("x", function (d) {
      return _this.scales.x(d.timestamp);
    })
    .attr("y", function (d) {
      return _this.scales.y(d.y);
    })
    .attr("height", function (d) {
      return _this.chart.height - _this.scales.y(d.y);
    })
    .attr("width", 3);

  if (
    this.data.station.showAllMeasurements === true ||
    this.data.station.showFiveMinutesMeasurements === true
  ) {
    selection.style("fill", function (d) {
      return _this.getColor(d.y);
    });
  }

  this.defs.clip.attr("width", this.chart.width);
  this.defs.totalClip.attr("width", this.chart.width);

  if (this.data.pinned) {
    this.elements.mouse
      .transition()
      .attr("transform", "translate(" + (this.scales.x(this.data.pinDate) - 2) + "," + 0 + ")");
  }

  if (this.precipitationInterval > 0) {
    this.elements.legendSpan.html(
      gettext("Precipitation in {{unit}} per {{interval}} hour")
        .replace("{{unit}}", cropr.l10n.unit_precipitation_display)
        .replace("{{interval}}", this.precipitationInterval)
    );
  } else {
    this.elements.legendSpan.html(
      gettext("Precipitation in {{unit}} per {{interval}} hour")
        .replace("{{unit}}", cropr.l10n.unit_precipitation_display)
        .replace("{{interval}}", "1")
    );
  }
};

// Not really DRY. The same as update besides the transitions
Chart.prototype._scroll = function () {
  var _this = this;

  this.elements.axis.x.call(this.axis.x);
  this.elements.axis.y.call(this.axis.y);
  this.elements.grid.x.call(this.axis.gridX);
  this.elements.grid.y.call(this.axis.gridY);

  this.elements.innerChart
    .selectAll(".bar.stack")
    .attr("x", function (d) {
      return _this.scales.x(d.timestamp);
    })
    .attr("y", function (d) {
      return _this.scales.y(d.y);
    });

  this.defs.clip.attr("width", this.chart.width);
  this.defs.totalClip.attr("width", this.chart.width);

  if (this.data.pinned) {
    this.elements.mouse.attr(
      "transform",
      "translate(" + (this.scales.x(this.data.pinDate) - 2) + "," + 0 + ")"
    );
  }
};

Chart.prototype.resize = function (width, height) {
  if (width) {
    this.options.width = width;
    this.chart.width = this.options.width - (this.chart.margin.left + this.chart.margin.right);
  }

  if (height) {
    this.options.height = height;
    this.chart.width = this.options.height - (this.chart.margin.top + this.chart.margin.bottom);
  }

  this.elements.svg.style("width", this.options.width);
  this.elements.svg.style("height", this.options.height);

  // Update the scales
  this._setScales();
  this._setAxis();
  this.update();
};

Chart.prototype.destroy = function () {
  this.elements.svg.remove();
};

Chart.prototype._setScales = function () {
  var domain = this.data.domain;

  this.scales = {
    x: d3.time.scale().range([0, this.chart.width]).domain(domain),

    y: d3.scale.linear().range([this.chart.height, 0]),
  };
};

Chart.prototype._setAxis = function () {
  var _this = this;

  // Calculate the amount of ticks
  var ticksY = Math.round(this.chart.height / 40);
  var ticksX = Math.round(this.chart.width / 150);

  this.axis = {
    x: d3.svg
      .axis()
      .scale(this.scales.x)
      .orient("bottom")
      .ticks(ticksX)
      .outerTickSize(0)
      .tickFormat(timeFormat.chart),

    y: d3.svg
      .axis()
      .scale(this.scales.y)
      .orient("left")
      .ticks(ticksY)
      .outerTickSize(0)
      .tickFormat(function (d) {
        return cropr.l10n_utils.mm_to_l10n(d) + " " + _this.options.i18n.unit;
      }),

    //    yRel: d3.svg.axis().scale(this.scales.yRel).orient('right').ticks(ticksY)
    //      .outerTickSize(0).tickFormat(function(d) { return d + '%'; }),

    gridX: d3.svg
      .axis()
      .scale(this.scales.x)
      .orient("bottom")
      .ticks(ticksX)
      .tickSize(-this.chart.height, 0, 0)
      .tickFormat(""),
    gridY: d3.svg
      .axis()
      .scale(this.scales.y)
      .orient("left")
      .ticks(ticksY * 2)
      .tickSize(-this.chart.width, 0, 0)
      .tickFormat(""),
  };
};

Chart.prototype._setDataShapes = function () {
  this.shapes = {};
};

Chart.prototype._compressBatched = function (data, redraw) {
  // Interval in hours
  // To be calulated
  var interval = 1; // Data is per kwartier, 4 intervals is een uur
  var barWidth = 3;

  // How many bars are going to fit insite the current chart
  var fittingBars = this.chart.width / barWidth;

  // How many hours is our current domain
  var domainStart = moment(this.data.domain[0]);
  var domainEnd = moment(this.data.domain[1]);

  var diff = domainEnd.diff(domainStart, "hours");

  while (fittingBars * interval < diff) {
    interval++;
  }

  if (interval > 12) {
    interval = 24;
  } else if (interval > 6) {
    interval = 12;
  } else if (interval > 4) {
    interval = 6;
  }

  // Only recalculate if neccesary
  //  if (this.precipitationInterval === interval) { // && !redraw) {
  //    return this.data.compressedData;
  //  }

  this.precipitationInterval = interval;

  var compressedData = [];
  var sum = 0;
  var maxTime = false;

  each(data, function (value) {
    var time = value.timestamp;
    if (!maxTime) {
      maxTime = new Date(time);
      var correction = time.getHours() % interval;
      maxTime.setMinutes(0);
      if (interval === 1) {
        maxTime.setTime(maxTime.getTime());
      } else {
        maxTime.setTime(maxTime.getTime() + (interval - correction) * 3600 * 1000);
      }
    }

    if (maxTime <= time) {
      // Measures are created at the end of the interval, so change the time of the measurement to half of the interval
      var averageTime = new Date(maxTime);
      averageTime.setTime(maxTime.getTime() - (interval * 3600 * 1000) / 2);

      compressedData.push({
        timestamp: averageTime,
        y: sum,
      });

      sum = 0;

      maxTime = false;
    }

    // @TODO: before adding the value to the sum, check if the measurement is active
    sum += value.y;
  });

  // The last push, with the remaining rain fall
  if (maxTime) {
    // Measures are created at the end of the interval, so change the time of the measurement to half of the interval
    var averageTime = new Date(maxTime);
    averageTime.setTime(maxTime.getTime() - (interval * 3600 * 1000) / 2);

    compressedData.push({
      timestamp: averageTime,
      y: sum,
    });
  }

  this.data.compressedData = compressedData;
  return compressedData;
};

Chart.prototype._drawClipPaths = function () {
  var defs;
  this.defs = defs = this.elements.svg.append("defs");

  this.defs.clip = defs
    .append("svg:clipPath")
    .attr("id", "clip-" + this.options.id)
    .append("svg:rect")
    .attr("x", 0)
    .attr("y", -8)
    .attr("height", this.chart.height + 8);

  this.defs.totalClip = defs
    .append("svg:clipPath")
    .attr("id", "clip-total-" + this.options.id)
    .append("svg:rect")
    .attr("x", "0")
    .attr("y", "-8")
    .attr("height", this.totalChart.height + 8);

  this.defs.mask = defs
    .append("svg:mask")
    .attr("id", "mask-" + this.options.id)
    .attr("width", this.chart.width)
    .attr("height", this.chart.height + 8)
    .attr("x", 0)
    .attr("y", -8);
};

export default Chart;
